/**
 * Fixture GROUP authoring - the one write path OPButtons has into OPBridge's
 * rig, and deliberately the narrowest one possible.
 *
 * ## Why groups are writable when nothing else about a fixture is
 *
 * Locked by the operator 2026-09-17 ("author yes"). The plan deferred the
 * full patch surface - add / delete / re-address a fixture - pending a
 * decision, and this is not a quiet expansion of it. Three reasons this one
 * field is different in kind:
 *
 *  - **A group tag names nothing about hardware.** Getting it wrong mislabels
 *    a target. It cannot mis-address a light, collide two fixtures'
 *    footprints, or re-home a fixture across the mesh. Address, mode, add and
 *    delete stay behind the Phase 3 decision precisely because they can.
 *  - **It is the highest-value target in the product.** "All wash lights" is
 *    one tap for a thing that otherwise costs several, and a target
 *    vocabulary the operator cannot extend goes stale the first time they add
 *    a light.
 *  - **The lossless converter already existed** before this needed it. See
 *    below - that ordering is load-bearing, not luck.
 *
 * ## THE HARD BOUNDARY
 *
 * Every function here changes `groups` and nothing else. Not the name, not
 * the mode, not the universe, not the address. If editing one of those turns
 * out to be wanted, that is the Phase 3 conversation, not a quiet widening of
 * this module.
 *
 * ## Why a whole-instance PUT is safe here and would NOT have been last week
 *
 * `PUT /fixtures/{id}` takes a WHOLE `FixtureInstance`, and OPBridge's update
 * path deliberately never re-stamps `output_node_id` ("the PUT path never
 * auto-stamps - the body owns it"). Until 2026-09-17 the PWA modelled eight of
 * OPBridge's eleven fields, so the first edit this app ever wrote would have
 * silently stripped the mesh owner (DEC-471), the venue-environment stamp
 * (DEC-470) and the operator's existing group tags - and the fixture would
 * have fallen back to "emit wherever a local driver exists". Phase 1 widened
 * the wire type to all eleven plus a passthrough bag and added
 * `toBridgeFixture` as the inverse of the reader. THAT is what makes this
 * module a two-line change instead of a data-loss incident, and it is the
 * reason the envelope came first.
 *
 * Every write here therefore starts from the fixture we READ, mutates exactly
 * `groups`, and sends the whole thing back through `toBridgeFixture`.
 *
 * ## Refresh, not optimism
 *
 * A successful PUT is followed by a re-read of `/fixtures`, rather than
 * patching the local list optimistically. It costs one request on an
 * operation that happens at authoring time, never in a gig's hot path, and it
 * means the group list an operator sees is the one OPBridge actually stored -
 * including any normalisation it applied. An optimistic update that diverged
 * would be invisible until the next reconnect.
 */

import type { FixtureInstance } from '@opbuttons/schema';
import { bridges, getConnection } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { toBridgeFixture, refreshBridgeFixtures } from '../fixtures/bridgeSync.ts';

export interface GroupWriteResult {
  ok: boolean;
  /** Human-readable reason on failure. Never an exception - callers are
   *  pointer handlers. */
  error?: string;
}

function primaryConnection() {
  const primary = bridges().find((b) => b.is_primary) ?? bridges()[0];
  return primary ? getConnection(primary.id) : undefined;
}

/**
 * Write one fixture's group list back to OPBridge.
 *
 * `next` replaces the whole list: de-duplicated, trimmed, empties dropped,
 * and sorted so two rigs that hold the same groups serialise identically
 * (a stable order makes an LWW merge across peers a no-op when nothing
 * actually changed, instead of a write that looks like a change).
 */
export async function setFixtureGroups(
  fixture: FixtureInstance,
  next: string[],
): Promise<GroupWriteResult> {
  const conn = primaryConnection();
  if (!conn || conn.status() !== 'connected') {
    return { ok: false, error: 'No OPBridge connected - groups live on the rig, not on this iPad' };
  }

  const cleaned = [...new Set(next.map((g) => g.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  // Start from the fixture we READ, change only `groups`. The spread carries
  // outputNodeId / environmentId / transport / passthrough through untouched;
  // see this module's doc comment for what happens without it.
  const body = toBridgeFixture({ ...fixture, groups: cleaned });

  try {
    await conn.httpClient().updateFixture(fixture.id, body);
  } catch (err) {
    if (err instanceof OpbridgeError) {
      // 403 is its own sentence: the fix is a scope grant on another machine,
      // not anything the operator can do here. Same discrimination
      // bridgeSync's status model makes for the read side.
      if (err.httpStatus === 403) {
        return { ok: false, error: 'OPBridge will not let this pairing edit the rig (missing scope)' };
      }
      return { ok: false, error: `${err.kind}: ${err.message}` };
    }
    return { ok: false, error: (err as Error).message };
  }

  await refreshBridgeFixtures();
  return { ok: true };
}

/** Add one tag to a fixture. No-op (and reports success) when it already has
 *  it, so a double-tap is not an error. */
export function addFixtureToGroup(fixture: FixtureInstance, group: string): Promise<GroupWriteResult> {
  const current = fixture.groups ?? [];
  if (current.includes(group)) return Promise.resolve({ ok: true });
  return setFixtureGroups(fixture, [...current, group]);
}

/** Remove one tag from a fixture. */
export function removeFixtureFromGroup(fixture: FixtureInstance, group: string): Promise<GroupWriteResult> {
  const current = fixture.groups ?? [];
  if (!current.includes(group)) return Promise.resolve({ ok: true });
  return setFixtureGroups(fixture, current.filter((g) => g !== group));
}

/**
 * Rename a group across every fixture that carries it.
 *
 * N writes, one per affected fixture, run SEQUENTIALLY rather than in
 * parallel. A rename is the one group operation that can leave the rig
 * half-converted, and serialising means a failure partway through leaves a
 * knowable state ("the first three moved") that the returned count can
 * report, instead of an arbitrary subset. It is authoring-time work on a
 * handful of fixtures; the wall-clock cost is not worth the ambiguity.
 */
export async function renameGroup(
  rig: FixtureInstance[],
  from: string,
  to: string,
): Promise<{ ok: boolean; moved: number; total: number; error?: string }> {
  const clean = to.trim();
  if (!clean) return { ok: false, moved: 0, total: 0, error: 'A group needs a name' };
  const affected = rig.filter((f) => (f.groups ?? []).includes(from));
  let moved = 0;
  for (const f of affected) {
    const next = (f.groups ?? []).map((g) => (g === from ? clean : g));
    const res = await setFixtureGroups(f, next);
    if (!res.ok) return { ok: false, moved, total: affected.length, ...(res.error ? { error: res.error } : {}) };
    moved += 1;
  }
  return { ok: true, moved, total: affected.length };
}

/** Delete a group by removing its tag from every fixture. Sequential for the
 *  same reason as rename. There is no group registry to delete FROM - a group
 *  exists exactly as long as a fixture carries its tag. */
export async function deleteGroup(
  rig: FixtureInstance[],
  group: string,
): Promise<{ ok: boolean; removed: number; total: number; error?: string }> {
  const affected = rig.filter((f) => (f.groups ?? []).includes(group));
  let removed = 0;
  for (const f of affected) {
    const res = await removeFixtureFromGroup(f, group);
    if (!res.ok) return { ok: false, removed, total: affected.length, ...(res.error ? { error: res.error } : {}) };
    removed += 1;
  }
  return { ok: true, removed, total: affected.length };
}
